import { PaginatedQueryParams } from './api'

export enum VoipProfileSubjectType {
  Bot = 'bot',
  User = 'user',
}

/** A line the profile owns, as summarized on VoipProfile.extensions. */
export interface VoipProfileExtension {
  id: number
  name: string
  /**
   * Sent as a string (`"100"`), confirmed against a production list response on
   * 2026-08-31. It was declared a number here until then, which silently
   * emptied the agents list Extension column (AI-242).
   */
  number: string
  type: string
  pbx_id: number
}

export interface VoipProfile {
  id: number
  first_name: string
  last_name: string
  full_name: string
  subject_type: VoipProfileSubjectType
  subject_id: string
  /**
   * The lines assigned to this profile. Serialized on every read INCLUDING the
   * create response, which is what lets a repeat create tell "fresh profile"
   * from "profile that already has a line" without a second request.
   */
  extensions: VoipProfileExtension[]
  created_at: string
  updated_at: string
}

export interface VoipProfilesQueryParams extends PaginatedQueryParams {
  subject_type?: VoipProfileSubjectType
  pbx_id?: number
}

/**
 * Body for POST /app/v1/voip-profiles/ (AI-134).
 *
 * `subject_id` is the **ai-configuration `agents.id` UUID**, not the Telnyx
 * assistant id — confirmed with Scott and Brandon on 2026-08-26. No host
 * identifier crosses into mango-api, which is what keeps the publish spec's
 * no-host-id rule intact.
 *
 * The server enforces the 1:1 rule (`unique_voip_profile_subject`) and returns
 * the EXISTING profile rather than erroring when the subject pair repeats, so a
 * retried Activate is idempotent. `pbx` is write-only and create-only.
 */
export interface CreateVoipProfileInput {
  first_name: string
  last_name: string
  subject_type: VoipProfileSubjectType
  subject_id: string
  pbx: number
}

/**
 * GET /internal/v1/pbxes/{pbx_id}/voip-profile-uac-settings/?subject_id=...
 *
 * The SIP credentials for the extension line a profile owns. Resolved as
 * subject_id → voip_profile → the line holding it, so the line must already be
 * assigned to the profile or this 400s.
 *
 * `transport`, `expiration_sec` and the port on `outbound_proxy` are
 * mango-api's configuration — forward them as they arrive rather than
 * hardcoding, or the day mango-api changes one an office silently stops
 * registering.
 *
 * Carries the line's SIP secret: never log or persist this.
 */
export interface VoipProfileUacSettings {
  username: string
  password: string
  proxy: string
  auth_username?: string
  from_user?: string
  outbound_proxy?: string
  transport?: string
  expiration_sec?: number
  /**
   * Present only when the PBX's server group has one. The publish contract's
   * SipRegistration is `additionalProperties: false` and has no field for it,
   * so it is deliberately dropped on the way through.
   */
  alternate_outbound_proxy?: string
}
